import { useState } from 'react';
import './Tools.scss';
import { getOfflineFailures, saveOfflineFailures } from '../../hooks/use-logistics-api';
import { QueuedEvent } from '../../types/logistics';

const extractHttpStatus = (lastError?: string): string | undefined => {
  if (!lastError) return undefined;
  const m = lastError.match(/\b([45]\d{2})\b/);
  return m ? `HTTP ${m[1]}` : undefined;
};

const formatTimestamp = (createdAt: number): string =>
  new Date(createdAt).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

const Tools = () => {
  const [failures, setFailures] = useState<QueuedEvent[]>(() => getOfflineFailures());
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopyRequestId = (requestId: string, entryId: string) => {
    navigator.clipboard.writeText(requestId).then(() => {
      setCopied(entryId);
      setTimeout(() => setCopied(null), 2000);
    }).catch(() => undefined);
  };

  const handleClearFailures = () => {
    saveOfflineFailures([]);
    setFailures([]);
  };

  return (
    <div className="tools-page">
      <h1 className="tools-page__title">Diagnostic Tools</h1>

      <section className="tools-page__section">
        <div className="tools-page__section-header">
          <h2 className="tools-page__section-title">Offline Queue Failures</h2>
          {failures.length > 0 && (
            <button className="tools-page__clear-btn" onClick={handleClearFailures}>
              Clear all
            </button>
          )}
        </div>

        {failures.length === 0 ? (
          <p className="tools-page__empty">No failures recorded.</p>
        ) : (
          <ul className="tools-page__failure-list">
            {[...failures].reverse().map((f) => {
              const codeLabel = f.failureCode ?? extractHttpStatus(f.lastError) ?? '—';
              return (
                <li key={f.id} className="failure-entry">
                  <div className="failure-entry__meta">
                    <span className="failure-entry__timestamp">{formatTimestamp(f.createdAt)}</span>
                    <span className="failure-entry__type">{f.type.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="failure-entry__code-row">
                    <span className="failure-entry__code">{codeLabel}</span>
                    {f.requestId && (
                      <span className="failure-entry__request-id">
                        <code>{f.requestId}</code>
                        <button
                          className={`failure-entry__copy-btn${copied === f.id ? ' failure-entry__copy-btn--copied' : ''}`}
                          onClick={() => handleCopyRequestId(f.requestId!, f.id)}
                          aria-label="Copy request ID"
                        >
                          {copied === f.id ? 'Copied' : 'Copy ID'}
                        </button>
                      </span>
                    )}
                  </div>
                  {f.lastError && (
                    <p className="failure-entry__message">{f.lastError}</p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
};

export default Tools;
